
import Vue, { PropType } from 'vue'
import {
  customSitePath,
  foundationsHymnUrl,
  foundationsPsalterRootUrl,
  siteHymnUrl,
} from '~/assets/ts/utils/urls'
import { HymnApi, TuneApi } from '~/apiclient/apihymns'
import FancyDropdown, {
  FancyDropdownOption,
} from '~/components/_general/FancyDropdown.vue'
import BackLink from '~/components/_general/BackLink.vue'
import LoadingElement from '~/components/_general/LoadingElement.vue'
import PlayerElement from '~/components/player/Element.vue'
import InlineIcon from '~/components/_general/InlineIcon.vue'
import SiteFilteredSermonList from '~/components/site/FilteredSermonList.vue'
import {
  DefaultSermonPageSize,
  SermonRequestOptions,
  SermonSortOptions,
} from '~/apiclient/apisermons'
import SiteTitledSection from '~/components/site/TitledSection.vue'
import SiteButton from '~/components/site/Button.vue'
import PlayerAudioButton from '~/components/player/AudioButton.vue'
import { Sermon } from '~/models/sermon'
import NavigationTabList from '~/components/_general/NavigationTabList.vue'
import { NavigationTabs } from '~/assets/ts/enums'
import { DeviceMetadata, GetHymnalMetadata } from '~/assets/ts/utils/player'

enum HymnTab {
  None = '',
  Lyrics = 'lyrics',
  Passage = 'passage',
  History = 'history',
  Sermons = 'sermons',
}

export default Vue.extend({
  name: 'HymnalHymnInfo',
  components: {
    NavigationTabList,
    PlayerAudioButton,
    SiteButton,
    SiteTitledSection,
    SiteFilteredSermonList,
    InlineIcon,
    PlayerElement,
    FancyDropdown,
    LoadingElement,
    BackLink,
  },
  props: {
    hymn: {
      type: Object as PropType<HymnApi>,
      default: undefined,
    },
    checkPsalter: {
      type: Boolean,
      required: true,
    },
    foundations: {
      type: Boolean,
    },
  },
  data() {
    let t = HymnTab.None
    if (this.hymn?.verses) {
      t = HymnTab.Lyrics
    } else if (this.hymn?.history) {
      t = HymnTab.History
    } else if (this.hymn?.bibleVerse) {
      t = HymnTab.Passage
    } else if (this.hymn?.psalter) {
      t = HymnTab.Sermons
    }

    const tune = this.hymn?.tunes[0] ?? undefined

    return {
      tab: t as HymnTab,
      hymnTunesPojo: [] as TuneApi[],
      selectedTune: tune as TuneApi | undefined,
      showTuneSelector: true,
    }
  },
  async fetch() {
    if (!this.selectedTune || !this.selectedTune.metricID) {
      this.showTuneSelector = false
      return
    }
    const { results } = await this.$apiClient.getTunesByMetric(
      this.selectedTune.metricID
    )

    if (results.length > 1) {
      this.hymnTunesPojo = results
    } else {
      this.showTuneSelector = false
    }

    this.getTuneFromUrl()
  },
  computed: {
    tabs(): NavigationTabs {
      const list = [] as NavigationTabs
      const hymn = this.hymn
      if (!hymn) return list
      if (hymn.verses) {
        list.push({
          title: this.$t('Lyrics'),
          slug: HymnTab.Lyrics,
        })
      }
      if (hymn.history) {
        list.push({
          title: this.$t('History'),
          slug: HymnTab.History,
        })
      }
      if (hymn.bibleVerse) {
        list.push({
          title: this.$t('Bible'),
          slug: HymnTab.Passage,
        })
      }
      if (hymn.psalter) {
        list.push({
          title: this.$t('Sermons'),
          slug: HymnTab.Sermons,
        })
      }
      return list
    },
    playerMetadata(): DeviceMetadata {
      return GetHymnalMetadata(this.hymn)
    },
    SermonSortOptions() {
      return SermonSortOptions
    },
    hymnTunes(): TuneApi[] | undefined {
      if (!this.hymnTunesPojo.length) return undefined
      return [...this.hymnTunesPojo].sort((a: TuneApi, b: TuneApi) => {
        return a.description.localeCompare(b.description)
      })
    },
    tuneOptions(): FancyDropdownOption[] {
      if (!this.hymnTunes) return []
      return this.hymnTunes.map((tune: TuneApi) => {
        return {
          title: tune.description,
          value: tune.tuneID,
        } as FancyDropdownOption
      }) as FancyDropdownOption[]
    },
    readingAudioStream(): string | undefined {
      if (!this.hymn?.readingSermon) return undefined
      const s = new Sermon(this.hymn.readingSermon)
      if (!s.hasAudio) return undefined
      return s.media?.highestAudio?.streamURL
    },
    isLyrics(): boolean {
      return this.tab === HymnTab.Lyrics
    },
    isPassage(): boolean {
      return this.tab === HymnTab.Passage
    },
    isHistory(): boolean {
      return this.tab === HymnTab.History
    },
    isSermons(): boolean {
      return this.tab === HymnTab.Sermons
    },
    backUrl(): string {
      if (this.foundations) {
        return customSitePath(this, 'psalter', foundationsPsalterRootUrl)
      }
      if (this.foundations) return foundationsPsalterRootUrl
      return this.checkPsalter ? 'psalter' : 'hymnal'
    },
    fullscreenUrl(): string {
      if (!this.hymn) return ''
      if (this.foundations) {
        return foundationsHymnUrl(this, this.hymn, 'fullscreen/')
      }
      return siteHymnUrl(this.hymn, 'fullscreen/')
    },
    queryTuneID(): string | undefined {
      return (this.$route.query.tuneID as string) ?? undefined
    },
    psalmChapter(): string {
      if (!this.hymn || !this.hymn.psalter) return ''
      return this.hymn.title.split(' ')[1]
    },
    psalterSermonOptions(): SermonRequestOptions {
      if (!this.hymn || !this.hymn.psalter) return {} as SermonRequestOptions
      return {
        book: 'PSA',
        chapter: this.psalmChapter,
        pageSize: DefaultSermonPageSize,
      } as SermonRequestOptions
    },
  },
  watch: {
    '$route.query.tuneID': {
      deep: true,
      handler: function () {
        this.getTuneFromUrl()
      },
    },
  },
  methods: {
    changeTune(tuneID: string) {
      this.$navigateTo({
        query: {
          tuneID,
        },
      })
    },
    getTuneFromUrl() {
      if (!this.queryTuneID) return

      this.selectedTune =
        this.hymnTunes?.find(
          (tune: TuneApi) => tune.tuneID === this.queryTuneID
        ) ??
        this.hymn?.tunes[0] ??
        undefined
    },
    setTab(tab: HymnTab) {
      this.tab = tab
      this.$emit('sermontab', this.tab === HymnTab.Sermons)
    },
  },
})
